import { parseErrorBody } from '@/services/http';

/**
 * Decision logic for the PO "Reload images" action (AS-10987).
 *
 * WHY A HELPER RATHER THAN METHODS ON PurchaseOrderDetail.vue
 *
 * `tests/unit/vue-shim.js` stubs `.vue` imports (`require.extensions['.vue'] =
 * () => {}`), so a Vue class-component getter cannot be invoked from a spec —
 * the only thing a spec can do with the component is assert its source text.
 * Source guards are fragile (see MR !2839, which took three review rounds to
 * stop a regex-based guard passing on vulnerable code), so every decision that
 * can be expressed as a pure function lives here and is tested for real. The
 * component keeps only the wiring, and a thin source guard proves it delegates.
 *
 * Story: AS-10987 (parent AS-10982)
 */

/**
 * Actions that belong in the overflow menu at ALL times, independent of the
 * collapse heuristic.
 *
 * Needed because `shouldCollapseButtons` is `actionButtonCount >= 4` — a count
 * of AVAILABLE ACTIONS, not a viewport measurement (nothing viewport-derived
 * feeds it). When a PO has fewer than four actions, `moreMenuButtons` returns
 * `[]`, so an action that should live only in the overflow menu had nowhere to
 * go. That hits exactly the statuses this feature is for: a completed PO has
 * three actions (reopen, return, duplicate) and a cancelled one has one
 * (duplicate).
 */
export const OVERFLOW_ONLY_ACTIONS: readonly string[] = ['reloadImages'];

export interface SecondaryActionInputs {
  canAddProducts: boolean;
  canImportItems: boolean;
  /** `moreMenuButtons.length` — passed in, never derived or mutated here. */
  moreMenuButtonCount: number;
}

/**
 * Whether the overflow ("More") menu should render at all.
 *
 * The `v-menu` is wrapped in `v-if="hasSecondaryActions"`, so on a completed or
 * cancelled PO the menu element was never created and adding an item to it could
 * not have helped.
 *
 * `OVERFLOW_ONLY_ACTIONS` is non-empty, so this is effectively always true now.
 * It is still written as a disjunction rather than `return true` so the reason
 * survives: the moment that list is emptied, the old behaviour comes back rather
 * than the menu rendering permanently empty.
 *
 * Deliberately takes the collapse inputs as a plain value object and returns a
 * boolean — no side effects, nothing else consulted. That is what makes AC 6
 * ("existing collapse behaviour unchanged") true by construction: this function
 * cannot influence `shouldCollapseButtons`, `inlineButtons` or
 * `moreMenuButtons`, which are left untouched.
 */
export function hasSecondaryActions(inputs: SecondaryActionInputs): boolean {
  return (
    OVERFLOW_ONLY_ACTIONS.length > 0 ||
    inputs.canAddProducts ||
    inputs.canImportItems ||
    inputs.moreMenuButtonCount > 0
  );
}

export interface ReloadImagesResult {
  scanned: number;
  updated: number;
  stillMissing: number;
  skipped: number;
  bySource: { olap: number; shopify: number };
}

const items = (n: number): string => (n === 1 ? '1 item' : `${n} items`);

/**
 * The snackbar line for a reload outcome.
 *
 * Two distinct ways a line stays blank, worded separately because the merchant's
 * next step differs:
 *   stillMissing — the variant exists, Shopify has no image. Fixable in Shopify.
 *   skipped      — the line is not linked to a variant at all (a manually-typed
 *                  line). NOT fixable in Shopify; nothing can ever resolve it.
 *
 * Reporting plain success over either is the behaviour AS-10982 was reported
 * for, so neither collapses into "All images are already up to date".
 */
export function buildReloadImagesMessage(result?: ReloadImagesResult | null): string {
  // Guard the OBJECT as well as its fields: `post()` returns the parsed body and
  // a response without the usual envelope leaves this undefined, which would
  // throw inside the caller's try and report a successful reload as a failure.
  const r = (result || {}) as Partial<ReloadImagesResult>;
  const scanned = r.scanned || 0;
  const updated = r.updated || 0;
  const stillMissing = r.stillMissing || 0;
  const skipped = r.skipped || 0;

  const parts: string[] = [];

  if (updated > 0) {
    parts.push(`Updated ${updated} of ${scanned} images.`);
  } else if (stillMissing > 0 || skipped > 0) {
    parts.push('No images could be updated.');
  } else {
    return 'All images are already up to date.';
  }

  if (stillMissing > 0) {
    parts.push(`${items(stillMissing)} ${stillMissing === 1 ? 'has' : 'have'} no image in Shopify either.`);
  }

  if (skipped > 0) {
    parts.push(
      `${items(skipped)} ${skipped === 1 ? 'is' : 'are'} not linked to a product, ` +
      `so ${skipped === 1 ? 'it cannot' : 'they cannot'} have an image.`,
    );
  }

  return parts.join(' ');
}

/**
 * Recover the server's error message from an axios rejection.
 *
 * WHY THIS IS NOT `error.response.data.message`
 *
 * `ax` is created with a `transformResponse` that returns `data` untouched
 * (`services/http.ts:240-248`) — which is why `post()` has to call
 * `JSON.parse(res.data)` itself. Nothing parses the ERROR body, so
 * `error.response?.data?.message` is `undefined` for every JSON error this API
 * returns, and each catch block in PurchaseOrderDetail.vue silently falls back
 * to its own generic string. `extractApiErrorMessage` does not rescue it either:
 * it special-cases `Blob`, then falls through to the same lookup.
 *
 * WHY `error.message` IS CONSULTED ONLY WHEN THERE IS NO RESPONSE
 *
 * axios sets `.message` on every rejection — "Request failed with status code
 * 502". Checking it before the caller's fallback made that fallback dead for all
 * HTTP errors, so an Express HTML 404 (warbler shipping before the endpoint) or a
 * proxy 502 showed the merchant raw HTTP plumbing instead of "Failed to reload
 * product images". With a response present the caller's wording is better; with
 * no response at all, "Network Error" / "timeout of 300000ms exceeded" genuinely
 * says more and there is no body to read. Found in review — the original specs
 * built error objects with no `.message`, which real axios never produces, so
 * they passed over the bug.
 */
export function parseApiErrorMessage(error: any, fallback: string): string {
  if (error && error.response) {
    // Shared with the axios interceptor rather than parsed again here — a second
    // copy of this logic drifting from that one is the very failure mode this
    // story is about.
    const body = parseErrorBody(error.response.data);
    if (body && typeof body.message === 'string' && body.message) {
      return body.message;
    }
    return fallback;
  }

  if (error && typeof error.message === 'string' && error.message) {
    return error.message;
  }

  return fallback;
}
